import { computeNextWindow } from './compute-next-window';
import type { ScheduleConfig, Window } from './types';

/**
 * Upper bound on `previewWindows` — an unbounded rule would otherwise expand
 * without limit. A runaway guard, not a UI bound: it is sized by the largest
 * horizon `findWindowOverlap` scans (48 monthly periods + 1). The UI preview
 * asks for far fewer.
 */
export const MAX_PREVIEW_WINDOWS = 49;

/**
 * Return the first `n` windows (periods `0..n-1`), independent of real history;
 * fewer than `n` if the rule exhausts first. For save-time validation and UI
 * preview. Throws `RangeError` if `n` is not an integer in
 * `0..MAX_PREVIEW_WINDOWS`, and (via `computeNextWindow`) on an invalid RRULE.
 */
export const previewWindows = (config: ScheduleConfig, n: number): Window[] => {
  if (!Number.isInteger(n) || n < 0 || n > MAX_PREVIEW_WINDOWS) {
    throw new RangeError(
      `previewWindows: n must be an integer between 0 and ${MAX_PREVIEW_WINDOWS}, got ${n}`
    );
  }

  const windows: Window[] = [];
  for (let i = 0; i < n; i++) {
    const next = computeNextWindow(
      config,
      i === 0 ? null : { PeriodIndex: i - 1 }
    );
    if (next === null) {
      break;
    }
    windows.push(next);
  }

  return windows;
};
